import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Pagination from "@amzn/awsui-components-console/pagination";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import SummaryCard from "../../../../components/summary/SummaryCard";
import { Theme } from "@amzn/meridian";
import Loading from "../../../../components/Loading";
import NoSummary from "../../../../components/summary/NoSummary";
import { fetchPaginatedSummaries, generateSummaryGroup, preProcessInClient } from "../../../../utils/summaryUtils";
import { sampleSummaryList } from "../../../../localtest/LocalhostTest";
import LogManager from "../../../../utils/logManager";
import AlertModal, { ALERT_VARIANT } from "../../../../components/alertModal/AlertModal";
import aimsTheme from "../../../../styles/meridian-theme-aims.json";
import { SESSION_STORAGE_KEYS } from "../../../../constants";

/**
 * SummariesTabInClient renders a paginated view of meeting summaries in the Zoom client.
 *
 * Behavior:
 * - Fetches summaries from the backend using `fetchSummaries`.
 * - Summaries are grouped by date (e.g., "Today", "Yesterday") using `generateSummaryGroup` or `preProcessInMeeting`.
 * - Each group is paginated into chunks of 4 summaries via `preProcessInClient`.
 * - Displays loading, error, or empty states as appropriate.
 *
 * UI:
 * - Displays a header box with the current group title.
 * - Renders 2x2 summary cards per page.
 * - Provides pagination controls to navigate between pages.
 *
 * Used in: AIMS Zoom app when user clicks the "Summaries" tab from the client
 */
const SummariesTabInClient = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryGroups, setSummaryGroups] = useState(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
    return cached && cached !== "undefined" ? JSON.parse(cached) : {};
  });
  const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
    return cached && cached !== "undefined" ? JSON.parse(cached) : null;
  });
  const [isEndOfData, setIsEndOfData] = useState(
    sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA) === "true"
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [rawSummaries, setRawSummaries] = useState(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES);
    return cached && cached !== "undefined" ? JSON.parse(cached) : [];
  });

  const onViewMoreClick = (summaryData) => {
    navigate(`/client/summary/${summaryData.aimsUuid}`, {
      state: {
        summary: summaryData,
        fromPage: currentPage,
        isAttendanceReportEnabled: props.isAttendanceReportEnabled,
        isVerboseSummariesEnabled: props.isVerboseSummariesEnabled,
      },
    });
  };

  // Function to load more summaries when clicking next on the last page
  const loadMoreSummaries = async () => {
    if (isLoadingMore || isEndOfData) return;
    
    setIsLoadingMore(true);
    try {
      // Fetch more summaries
      const result = await fetchPaginatedSummaries(
        false, 
        lastEvaluatedKey, 
        10,
        {
          setSummaryGroups,
          setRawSummaries,
          setLastEvaluatedKey,
          setIsEndOfData
        }
      );
      
      if (result.summaryListJson.length === 0) {
        setIsLoadingMore(false);
        return;
      }
      
      // Get updated groups and pages
      const updatedGroups = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE) || "{}");
      const newProcessedPages = preProcessInClient(updatedGroups);
      setPages(newProcessedPages);
    } catch (error) {
      LogManager.error("Failed to fetch more summaries:", error);
      setShowServerError(true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Check if we have data in the cache and set initial state
  useEffect(() => {
    const returnPage = props.returnPage || location.state?.returnPage;
    const hasCachedData = Object.keys(summaryGroups).length > 0;
    if (window.location.href.includes("http://localhost:3000/")) {
      const groups = generateSummaryGroup(sampleSummaryList);
      setSummaryGroups(groups);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE, JSON.stringify(groups));
      const processedPages = preProcessInClient(groups);
      setPages(processedPages);
    }
    
    // Set initial state based on whether we have data
    setIsSummaryEmpty(!hasCachedData && window.location.href !== "http://localhost:3000/");
    setIsLoading(false);

    // Handle return page
    if (returnPage && returnPage >= 1) {
      setCurrentPage(returnPage);
    } else {
      setCurrentPage(1);
    }
  }, []);

  // Update component when prefetch completes and fresh data is available
  useEffect(() => {
    if (props.isPrefetchComplete && window.location.href !== "http://localhost:3000/") {
      // Sync component state with fresh data from sessionStorage after prefetch completes
      const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
      if (cached && cached !== "undefined") {
        const groups = JSON.parse(cached);
        
        // Update display data
        setSummaryGroups(groups);
        const processedPages = preProcessInClient(groups);
        setPages(processedPages);
        setIsSummaryEmpty(Object.keys(groups).length === 0);
        
        // Sync pagination state to handle race condition where component may have 
        // initialized with stale values before sessionStorage was cleared
        const cachedKey = sessionStorage.getItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
        if (cachedKey && cachedKey !== "undefined") {
          setLastEvaluatedKey(JSON.parse(cachedKey));
        }
        const endOfData = sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA) === "true";
        setIsEndOfData(endOfData);
      }
    }
  }, [props.isPrefetchComplete]);

  useEffect(() => {
    const hasSummaries = Object.keys(summaryGroups).length > 0;
    setIsSummaryEmpty(!hasSummaries);
  }, [summaryGroups]);

  // Show loading while waiting for prefetch to complete
  if (!props.isPrefetchComplete) return <Loading />;
  
  if (isLoading && !isLoadingMore) return <Loading />;
  if (showServerError) {
    return <AlertModal variant={ALERT_VARIANT.COMMON_ERROR} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />;
  }

  if (isSummaryEmpty || pages.length === 0) {
    return (
      <Column spacingInset="600 none none none">
        <Row width="660px" alignmentHorizontal="center">
          <NoSummary />
        </Row>
      </Column>
    );
  }

  const { groupTitle, summaries } = pages[currentPage - 1];

  return (
    <Column spacing="400" spacingInset="none" alignmentVertical="top" alignmentHorizontal="start">
      {/*Date box*/}
      <Box spacingInset="500 none none 500">
        <Text type="h100">
          {groupTitle}
        </Text>
      </Box>

      {/* Summary Cards */}
      <Box minHeight={362} width="660px" spacingInset="none none none 400">
        <Column spacing="none" spacingInset="none">
          {[0, 1].map((rowIndex) => (
            <Row key={rowIndex} spacing="none" spacingInset="none">
              {[0, 1].map((colIndex) => {
                const index = rowIndex * 2 + colIndex;
                const summary = summaries[index];
                if (!summary) return null;
                return (
                  <Box key={`${summary.aimsUuid}-${index}`} spacingInset="300">
                    <SummaryCard summary={summary} onViewMoreClick={onViewMoreClick} />
                  </Box>
                );
              })}
            </Row>
          ))}
        </Column>
      </Box>

      {/*Pagination Box*/}
      <Box spacingInset="none" width="660px">
        <Row spacing="none" spacingInset="none" alignmentHorizontal="end">
          {isLoadingMore &&
            <Theme palette={aimsTheme}>
              <Loader size="small" />
            </Theme>
          }
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={pages.length}
            onChange={({ detail }) => {
              // Ensure currentPage is valid
              if (detail.currentPageIndex <= pages.length) {
                setCurrentPage(detail.currentPageIndex);
              }
              // Check if we need to load more summaries
              if (detail.currentPageIndex == pages.length && !isEndOfData) {
                loadMoreSummaries();
              }
            }}
            openEnd
          />
        </Row>
      </Box>
    </Column>
  );
};

export default SummariesTabInClient;
